import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import SimpleTable from '../../components/SimpleTable';

import { feedbackGrid } from './styles';
import { Header } from '../../components';
import { FeedbackStore } from '../../stores/feedback/feedbackStore';
import { RatingStore } from '../../stores/rating/ratingStore';
import { RatingAdmResponse } from '../../services/rating';
import { Manager } from '../../core';
import { Colors } from '../../styles';

const StarDisplay = ({ rate, size = 20 }: { rate: number; size?: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <span key={i} style={{ color: i <= rate ? '#F8BB03' : '#d1d5db', fontSize: size }}>
        ★
      </span>
    );
  }
  return <span style={{ display: 'inline-flex', gap: 2 }}>{stars}</span>;
};

const RatingCard = ({ rating }: { rating: RatingAdmResponse }) => {
  const [open, setOpen] = useState(false);
  const hasFeedback = !!rating.feedback && rating.feedback.trim().length > 0;

  return (
    <div
      className="bg-white dark:bg-[#33373E] rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden transition-all duration-200"
      style={{ marginBottom: 12 }}
    >
      <div
        className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        onClick={() => hasFeedback && setOpen(!open)}
      >
        <div className="flex items-center gap-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
            style={{ background: Colors.green_darker }}
          >
            {(rating.name || '?')[0].toUpperCase()}
          </div>
          <div>
            <p className="font-semibold text-gray-800 dark:text-gray-100 capitalize">
              {rating.name} {rating.surname}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
               {rating.plan || '—'} <span className="mx-1">•</span>
               {rating.cleaningDate || rating.comboDate || '—'} <span className="mx-1">•</span> 
               <span className={rating.teamName ? 'font-semibold text-blue-600 dark:text-blue-400' : 'italic'}>{rating.teamName || 'Sem equipe'}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <StarDisplay rate={rating.rate} size={18} />
          {hasFeedback && (
            <span
              className="text-gray-400 dark:text-gray-500 text-lg transition-transform duration-200"
              style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              ▼
            </span>
          )}
        </div>
      </div>

      {open && hasFeedback && (
        <div className="px-5 pb-4 pt-0 border-t border-gray-100 dark:border-gray-700">
          <p className="text-gray-600 dark:text-gray-300 italic text-sm mt-3">
            "{rating.feedback}"
          </p>
          <p className="text-xs text-gray-400 mt-2">{rating.createdAt}</p>
        </div>
      )}
    </div>
  );
};

export const Feedback = observer(() => {
  const { previewList } = FeedbackStore();
  const [loading, setLoading] = useState(false);
  const [showRatings, setShowRatings] = useState(true);

  useEffect(() => {
    const loadRatings = async () => {
      try {
        setLoading(true);
        await RatingStore().get();
      } catch (e) {
        Manager.RequestError('error.rating.handlerRequest', e);
      } finally {
        setLoading(false);
      }
    };
    loadRatings();
  }, []);

  const ratingList = RatingStore().list || [];
  const averageRate = RatingStore().getAverageRate();
  const averageRateNum = parseFloat(averageRate);

  // Team Ranking Logic
  const teamStatsMap = new Map<string, { totalRate: number, count: number }>();
  ratingList.forEach(r => {
    const tName = r.teamName;
    if (tName) {
        if (!teamStatsMap.has(tName)) {
            teamStatsMap.set(tName, { totalRate: 0, count: 0 });
        }
        const st = teamStatsMap.get(tName)!;
        st.totalRate += Number(r.rate);
        st.count += 1;
    }
  });

  const teamRanking = Array.from(teamStatsMap.entries()).map(([name, stats]) => ({
      name,
      avg: stats.totalRate / stats.count,
      count: stats.count
  })).sort((a, b) => b.avg - a.avg || b.count - a.count);

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white dark:bg-secondary-dark-bg dark:text-gray-200 rounded-3xl">
      <Header category="Página" title="Avaliações" />

      {/* Overview Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 items-start">
        {/* Overall Rating Summary */}
        <div
          className="p-6 rounded-2xl relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #465944 0%, #598A5D 100%)',
          }}
        >
          <div className="flex flex-col items-center justify-between gap-4">
            <div className="flex items-center gap-5 w-full justify-center lg:justify-start">
              <div className="text-center">
                <p className="text-5xl font-bold text-white">{averageRate}</p>
                <StarDisplay rate={Math.round(averageRateNum)} size={22} />
              </div>
              <div>
                <p className="text-white font-semibold text-lg">Classificação Média</p>
                <p className="text-green-200 text-sm">
                  {ratingList.length} avaliação{ratingList.length !== 1 ? 'ões' : ''} recebida{ratingList.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="flex gap-2 w-full justify-center lg:justify-start mt-2">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = ratingList.filter(r => r.rate === star).length;
                const pct = ratingList.length > 0 ? (count / ratingList.length) * 100 : 0;
                return (
                  <div key={star} className="flex flex-col items-center gap-1">
                    <span className="text-white text-xs font-medium">{star}★</span>
                    <div className="w-8 bg-white bg-opacity-20 rounded-full overflow-hidden" style={{ height: 60 }}>
                      <div
                        className="w-full rounded-full transition-all duration-500"
                        style={{
                          height: `${pct}%`,
                          background: '#F8BB03',
                          marginTop: `${100 - pct}%`,
                        }}
                      />
                    </div>
                    <span className="text-green-200 text-xs">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Team Rankings */}
        <div className="p-6 rounded-2xl bg-white dark:bg-[#33373E] border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden min-h-[188px]">
          <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200 mb-4 flex items-center gap-2">
            🏆 Ranking de Equipes
          </h3>
          {teamRanking.length === 0 ? (
            <div className="h-full flex items-center justify-center p-4">
               <p className="text-sm text-gray-400 italic">Nenhuma equipe recebeu avaliações ainda.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-h-[300px] overflow-y-auto pr-1">
              {teamRanking.map((t, i) => (
                <div key={t.name} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#20232A] rounded-xl border border-transparent dark:border-gray-700 hover:border-blue-200 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-sm shadow-sm ${i === 0 ? 'bg-yellow-500' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-amber-700' : 'bg-gray-300 dark:bg-gray-600'}`}>
                      {i + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 dark:text-gray-200 leading-tight">{t.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{t.count} avaliação{t.count !== 1 ? 'ões' : ''}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="flex items-center gap-1">
                      <span className="font-bold text-gray-800 dark:text-gray-200 text-sm">{t.avg.toFixed(1)}</span>
                      <span style={{color: '#F8BB03', fontSize: 14}}>★</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rating Cards - Accordion */}
      {ratingList.length > 0 && (
        <div className="mb-8">
          <div
            className="flex items-center justify-between mb-4 cursor-pointer"
            onClick={() => setShowRatings(!showRatings)}
          >
            <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200">
              Classificações dos Clientes ({ratingList.length})
            </h3>
            <span
              className="text-gray-400 text-lg transition-transform duration-200"
              style={{ transform: showRatings ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              ▼
            </span>
          </div>

          {showRatings && (
            <div className="transition-all duration-300">
              {ratingList.map((r) => (
                <RatingCard key={r.id} rating={r} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Existing Feedback Table */}
      <div className="mt-4">
        <h3 className="text-lg font-bold text-gray-700 dark:text-gray-200 mb-4">
          Feedbacks de Contato
        </h3>
        <SimpleTable columns={feedbackGrid as any} data={previewList} pageSize={10} />
      </div>
    </div>
  );
});
